import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCw, Maximize2 } from 'lucide-react';
import TumblerCrop from '@/components/TumblerCrop';

/**
 * BottleModal — luxury product viewer.
 *  • Click + drag horizontally to rotate the bottle in 3D (front ↔ back).
 *  • For collections with full 360° photography (Corporate Chaos),
 *    we use real front/back renders. Other collections fall back to a
 *    smooth rotateY of a single render.
 *  • Press Esc or click the backdrop to dismiss.
 */
export default function BottleModal({ collection, onClose }) {
  const [rotation, setRotation] = useState(0);
  const dragRef = useRef({ active: false, lastX: 0 });

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Auto-orbit when idle (gentle showroom feel) — pauses while dragging.
  useEffect(() => {
    if (!collection) return undefined;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!dragRef.current.active) {
        setRotation((r) => (r + dt * 18) % 360);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [collection]);

  if (!collection) return null;

  function onPointerDown(e) {
    dragRef.current = { active: true, lastX: e.clientX ?? e.touches?.[0]?.clientX ?? 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e) {
    if (!dragRef.current.active) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const dx = x - dragRef.current.lastX;
    dragRef.current.lastX = x;
    setRotation((r) => (r + dx * 0.5 + 360) % 360);
  }
  function onPointerUp() {
    dragRef.current.active = false;
  }

  const hasFull = collection.rotation?.hasFullRotation;
  // Brightness scales with rotation to simulate a turning surface in light.
  const sideShade = 0.78 + 0.22 * Math.abs(Math.cos((rotation * Math.PI) / 180));

  return (
    <AnimatePresence>
      <motion.div
        key="modal"
        data-testid="bottle-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 z-[100] flex items-center justify-center"
      >
        {/* Backdrop */}
        <motion.div
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/85 backdrop-blur-md"
        />

        {/* Body */}
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full h-full md:h-[90vh] md:max-w-[1200px] md:rounded-3xl overflow-hidden"
          style={{ backgroundColor: collection.bg, color: collection.fg }}
        >
          {/* Top bar */}
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-6 md:px-10 py-6">
            <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase opacity-70">
              <span>KIVO</span>
              <span className="h-px w-6" style={{ background: collection.accent }} />
              <span>{collection.name}</span>
            </div>
            <button
              data-testid="bottle-modal-close"
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-10 h-10 rounded-full flex items-center justify-center border border-current/30 hover:bg-current/10 transition-colors"
            >
              <X size={16} strokeWidth={1.4} />
            </button>
          </div>

          {/* Content grid */}
          <div className="absolute inset-0 grid grid-cols-1 lg:grid-cols-12 gap-0 pt-20 md:pt-24 pb-10">
            {/* Stage */}
            <div className="lg:col-span-7 relative flex items-center justify-center select-none touch-none">
              <div
                data-testid="bottle-modal-stage"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={onPointerUp}
                style={{ perspective: 1400 }}
                className="relative w-full h-[60vh] md:h-[70vh] flex items-center justify-center cursor-grab active:cursor-grabbing"
              >
                {/* Pedestal glow */}
                <div
                  className="absolute bottom-[14%] left-1/2 -translate-x-1/2 w-[55%] h-10 rounded-[50%] blur-3xl"
                  style={{ background: hasFull ? '#000' : `${collection.accent}88`, opacity: 0.45 }}
                />

                <motion.div
                  className="relative w-[260px] sm:w-[320px] md:w-[380px] h-[420px] md:h-[560px]"
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: `rotateY(${rotation}deg)`,
                  }}
                >
                  {hasFull ? (
                    <>
                      {/* Front render */}
                      <div
                        className="absolute inset-0"
                        style={{
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          backgroundImage: `url(${collection.rotation.front})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center 40%',
                          backgroundRepeat: 'no-repeat',
                          borderRadius: '18px',
                          filter: `brightness(${sideShade})`,
                        }}
                      />
                      {/* Back render — pre-rotated 180° on the same plane */}
                      <div
                        className="absolute inset-0"
                        style={{
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)',
                          backgroundImage: `url(${collection.rotation.back})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center 40%',
                          backgroundRepeat: 'no-repeat',
                          borderRadius: '18px',
                          filter: `brightness(${sideShade})`,
                        }}
                      />
                    </>
                  ) : (
                    <TumblerCrop
                      row={collection.sheet.row}
                      col={collection.sheet.col}
                      className="absolute inset-0 rounded-3xl"
                    />
                  )}
                </motion.div>

                {/* Top angle preview card (only if available) */}
                {hasFull && collection.rotation.top && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                    className="absolute top-6 right-6 hidden md:block"
                  >
                    <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">
                      Top View
                    </div>
                    <div
                      className="w-28 h-28 rounded-2xl bg-no-repeat bg-cover bg-center border border-current/20"
                      style={{ backgroundImage: `url(${collection.rotation.top})` }}
                    />
                  </motion.div>
                )}

                {/* Drag hint */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase opacity-50"
                >
                  <RotateCw size={12} strokeWidth={1.4} />
                  Drag to Rotate
                </motion.div>
              </div>
            </div>

            {/* Details panel */}
            <div className="lg:col-span-5 flex flex-col justify-between px-8 md:px-12 lg:pl-0 lg:pr-12 overflow-y-auto">
              <div>
                <div
                  className="text-[10px] tracking-[0.35em] uppercase mb-6"
                  style={{ color: collection.accent }}
                >
                  Collection · {collection.name}
                </div>
                <h2 className="font-display text-[clamp(2.25rem,4vw,3.5rem)] leading-[0.95] tracking-[-0.02em]">
                  {collection.quote.map((q, i) => (
                    <span key={i} className="block">
                      {q}
                    </span>
                  ))}
                </h2>

                <p className="mt-6 max-w-md text-[15px] leading-relaxed opacity-75">
                  {collection.sub}
                </p>

                {/* Spec list */}
                <dl className="mt-10 space-y-3 max-w-md">
                  {Object.entries(collection.specs).map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-baseline justify-between gap-6 border-b border-current/10 pb-3 text-[13px]"
                    >
                      <dt className="opacity-50 tracking-[0.15em] uppercase text-[10px]">{k}</dt>
                      <dd className="font-medium">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="mt-10 flex items-center gap-3">
                <a
                  href="#waitlist"
                  onClick={onClose}
                  data-testid="bottle-modal-waitlist-btn"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-[12px] tracking-[0.18em] uppercase transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: collection.fg, color: collection.bg }}
                >
                  Get Early Access
                  <span style={{ color: collection.accent }}>→</span>
                </a>
                <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase opacity-50">
                  <Maximize2 size={12} strokeWidth={1.4} />
                  700ml · 24oz
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
